'use client';

import { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/lib/context/AuthContext';
import { Clock, CheckCircle, XCircle, AlertCircle, Award, Lock, ListChecks, Calendar, RefreshCw } from 'lucide-react';
import TestHistory from './TestHistory';
import TestTakingOverlay from './TestTakingOverlay';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { plural } from '@/lib/i18n/pluralization';
import { useToast } from '@/lib/context/ToastContext';
import { Button } from '@/components/ui/button';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/queries/queryKeys';
import { useServerNow } from '@/lib/hooks/useServerNow';
import { resolveWindow, windowStatus } from '@/lib/availability/resolveWindow';
import type { WindowInput, EventTimes } from '@/lib/availability/types';

type TestStatus = 'open' | 'closed' | 'upcoming';

interface Test {
  id: string;
  title: string;
  description: string | null;
  type: string;
  passing_score: number | null;
  time_limit_minutes: number | null;
  max_attempts: number | null;
  available_from: string | null;
  available_until: string | null;
  available_from_anchor: 'absolute' | 'event_start' | 'event_end';
  available_until_anchor: 'absolute' | 'event_start' | 'event_end';
  /** Anchor-resolved window edges (ISO); use for display, not the raw columns. */
  resolved_from?: string | null;
  resolved_until?: string | null;
  status: TestStatus;
  question_count?: number;
}

interface UserTestAttempt {
  testId: string;
  bestScore: number | null;
  passed: boolean;
  attemptCount: number;
}

interface Question {
  id: string;
  question_text: string;
  type: 'multiple_choice' | 'single_choice' | 'true_false' | 'essay';
  points: number | null;
  explanation: string | null;
  options?: QuestionOption[];
}

interface QuestionOption {
  id: string;
  option_text: string;
  is_correct: boolean | null;
}

interface TestTakingProps {
  eventId: string;
  enrollmentId: string;
  /** Parent event start_date for resolving anchored test windows.
   *  Pulled from the page's main event query – avoids a duplicate fetch. */
  eventStartDate?: string | null;
  /** Parent event end_date for resolving anchored test windows. */
  eventEndDate?: string | null;
  contentTitle?: string;
  onTestComplete?: (result: { passed: boolean }) => void;
}

export default function TestTaking({ eventId, enrollmentId, eventStartDate = null, eventEndDate = null, contentTitle = '', onTestComplete }: TestTakingProps) {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [selectedTest, setSelectedTest] = useState<Test | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [score, setScore] = useState<number | null>(null);
  const [passed, setPassed] = useState<boolean | null>(null);
  const [reviewMode, setReviewMode] = useState(false);
  const [reviewAnswers, setReviewAnswers] = useState<Record<string, string | string[]>>({});
  const [overlayTest, setOverlayTest] = useState<{ test: Test; attemptCount: number } | null>(null);
  const supabase = createClient();
  const serverNow = useServerNow();

  const eventTimes: EventTimes = { start_date: eventStartDate, end_date: eventEndDate };

  // Build a WindowInput from a test row. Anchored edges (event_start/event_end)
  // resolve against the parent event at read time – the raw available_from/until
  // columns are stale snapshots for anchored edges (e.g. after duplication) and
  // must never be read directly for status or display.
  const toWindowInput = (test: {
    available_from: string | null;
    available_until: string | null;
    available_from_anchor?: 'absolute' | 'event_start' | 'event_end' | null;
    available_until_anchor?: 'absolute' | 'event_start' | 'event_end' | null;
  }): WindowInput => ({
    from: test.available_from,
    from_anchor: test.available_from_anchor ?? 'absolute',
    until: test.available_until,
    until_anchor: test.available_until_anchor ?? 'absolute',
  });

  // Fetch tests list via TanStack Query
  const { data: rawTests = [], isLoading: testsLoading } = useQuery<Omit<Test, 'status'>[]>({
    queryKey: queryKeys.events.tests(eventId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tests')
        .select('*, test_questions(count)')
        .eq('content_id', eventId)
        .order('sort_order');

      if (error) throw error;
      return (data || []).map(t => {
        const countArr = t.test_questions as unknown as { count: number }[];
        const { test_questions: _, ...rest } = t;
        return { ...rest, question_count: countArr?.[0]?.count ?? 0 };
      });
    },
    staleTime: 10 * 60 * 1000,
    enabled: !!eventId,
  });

  // Derive tests with current status + anchor-resolved window (time-dependent,
  // so computed from raw data rather than cached)
  const tests = rawTests
    .map(test => {
      const input = toWindowInput(test);
      const resolved = resolveWindow(input, eventTimes);
      return {
        ...test,
        status: windowStatus(input, eventTimes, serverNow),
        resolved_from: resolved.from ? resolved.from.toISOString() : null,
        resolved_until: resolved.until ? resolved.until.toISOString() : null,
      };
    })
    .sort((a, b) => {
      const order = { open: 0, closed: 1, upcoming: 2 };
      return order[a.status] - order[b.status];
    });

  // Fetch user test attempts via TanStack Query
  const testIds = rawTests.map(t => t.id);
  const { data: userAttemptsData, isLoading: attemptsLoading } = useQuery<Map<string, UserTestAttempt>>({
    queryKey: queryKeys.events.userTestAttempts(eventId, enrollmentId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('test_attempts')
        .select('test_id, score, passed')
        .eq('user_id', user!.id)
        .eq('enrollment_id', enrollmentId)
        .in('test_id', testIds);

      if (error) throw error;

      const attemptsMap = new Map<string, UserTestAttempt>();
      (data || []).forEach(attempt => {
        const existing = attemptsMap.get(attempt.test_id);
        if (!existing) {
          attemptsMap.set(attempt.test_id, {
            testId: attempt.test_id,
            bestScore: attempt.score,
            passed: attempt.passed || false,
            attemptCount: 1
          });
        } else {
          existing.attemptCount++;
          if (attempt.passed && !existing.passed) {
            existing.passed = true;
            existing.bestScore = attempt.score;
          } else if ((attempt.score ?? 0) > (existing.bestScore ?? 0)) {
            existing.bestScore = attempt.score;
          }
        }
      });
      return attemptsMap;
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!eventId && !!enrollmentId && !!user?.id && testIds.length > 0,
  });
  const userAttempts = userAttemptsData ?? new Map<string, UserTestAttempt>();

  const loading = testsLoading || (!!user?.id && !!enrollmentId && testIds.length > 0 && attemptsLoading);

  const fetchQuestions = useCallback(async (testId: string) => {
    // Check TanStack Query cache first
    const cached = queryClient.getQueryData<Question[]>(queryKeys.events.testQuestions(testId));
    if (cached) {
      setQuestions(cached);
      return;
    }

    const { data, error } = await supabase
      .from('test_questions')
      .select(`
        *,
        options:test_question_options(*)
      `)
      .eq('test_id', testId)
      .order('sort_order');

    if (!error && data) {
      queryClient.setQueryData(queryKeys.events.testQuestions(testId), data);
      setQuestions(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient]);

  const startTest = async (test: Test) => {
    // Don't allow retaking a passed test
    const userAttempt = userAttempts.get(test.id);
    if (userAttempt?.passed) {
      toast.warning(t('event.test.testAlreadyPassed'));
      return;
    }

    // Check attempts count from server
    if (!user?.id) return;
    const { data, error } = await supabase
      .from('test_attempts')
      .select('id')
      .eq('test_id', test.id)
      .eq('user_id', user.id)
      .eq('enrollment_id', enrollmentId);

    const attemptCount = !error && data ? data.length : 0;

    if (test.max_attempts && attemptCount >= test.max_attempts) {
      toast.warning(t('event.test.maxAttemptsReached'));
      return;
    }

    // Open overlay
    setOverlayTest({ test, attemptCount });
  };

  const handleOverlayComplete = async (result: { score: number; passed: boolean }) => {
    // Refetch test data BEFORE closing overlay so the UI updates atomically
    // (prevents the test from disappearing while attempt data is still loading)
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.events.tests(eventId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.events.userTestAttempts(eventId, enrollmentId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.events.testHistory(eventId, enrollmentId) }),
    ]);
    setOverlayTest(null);
    // Notify parent (event page) so it can refresh certificate status
    onTestComplete?.(result);
  };

  const handleOverlayClose = () => {
    setOverlayTest(null);
  };

  const viewAttemptResults = useCallback(async (attemptId: string, testId: string) => {
    if (!user?.id) return;

    const test = tests.find(t => t.id === testId);
    if (!test) return;

    const { data } = await supabase
      .from('test_attempts')
      .select('answers, score, passed')
      .eq('id', attemptId)
      .single();

    if (data) {
      await fetchQuestions(testId);
      setSelectedTest(test);
      setReviewAnswers((data.answers as Record<string, string | string[]>) || {});
      setReviewMode(true);
      setScore(data.score);
      setPassed(data.passed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, tests, fetchQuestions]);

  if (loading) {
    return <div className="text-center py-8">{t('event.test.loading')}</div>;
  }

  if (tests.length === 0) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">{t('event.test.noTestsAvailable')}</p>
        <p className="text-sm text-gray-500 mt-2">{t('event.test.checkBackLater')}</p>
      </div>
    );
  }

  if (reviewMode && selectedTest) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-white border border-gray-300 rounded-lg p-6 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">{selectedTest.title}</h2>
              {selectedTest.description && (
                <p className="text-gray-600 mt-1">{selectedTest.description}</p>
              )}
            </div>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
              passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              {passed ? <CheckCircle className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
              <span className="font-semibold">
                {passed ? t('event.testHistory.passed') : t('event.testHistory.failed')} – {score}%
              </span>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {questions.map((question, index) => {
            const userAnswer = reviewAnswers[question.id];
            const correctOptions = question.options?.filter(o => o.is_correct) || [];
            const correctOptionIds = correctOptions.map(o => o.id);

            return (
              <div key={question.id} className="bg-white border border-gray-300 rounded-lg p-6">
                <h3 className="font-semibold mb-4">
                  {t('testTaking.question')} {index + 1} ({question.points ?? 1} {plural(question.points ?? 1, t('testTaking.pointOne'), t('testTaking.pointFew'), t('testTaking.pointMany'))})
                </h3>
                <p className="text-gray-800 mb-4">{question.question_text}</p>

                {question.type === 'essay' ? (
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <p className="text-sm font-medium text-gray-500 mb-1">{t('event.test.yourAnswer')}:</p>
                    <p className="text-gray-800 whitespace-pre-wrap">{(userAnswer as string) || '–'}</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {question.options?.map((option) => {
                      const isCorrect = correctOptionIds.includes(option.id);
                      const isSelected = question.type === 'multiple_choice'
                        ? Array.isArray(userAnswer) && userAnswer.includes(option.id)
                        : userAnswer === option.id;

                      let borderClass = 'border-gray-200';
                      let bgClass = '';
                      if (isCorrect) {
                        borderClass = 'border-green-300';
                        bgClass = 'bg-green-50';
                      } else if (isSelected && !isCorrect) {
                        borderClass = 'border-red-300';
                        bgClass = 'bg-red-50';
                      }

                      return (
                        <label key={option.id} className={`flex items-start gap-3 p-3 border rounded-lg ${borderClass} ${bgClass}`}>
                          <input
                            type={question.type === 'multiple_choice' ? 'checkbox' : 'radio'}
                            checked={isSelected}
                            disabled
                            className="h-4 w-4 mt-0.5 flex-shrink-0"
                          />
                          <span className="flex-1 break-words">{option.option_text}</span>
                          {isCorrect && (
                            <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                          )}
                          {isSelected && !isCorrect && (
                            <XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
                          )}
                        </label>
                      );
                    })}
                  </div>
                )}

                {question.explanation && (
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">{question.explanation}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-8 flex justify-start">
          <Button
            variant="secondary"
            onClick={() => {
              setReviewMode(false);
              setSelectedTest(null);
              setQuestions([]);
              setReviewAnswers({});
              setScore(null);
              setPassed(null);
            }}
          >
            {t('event.test.backToTests')}
          </Button>
        </div>
      </div>
    );
  }

  // Group tests by status
  const openTests = tests.filter(t => t.status === 'open' && !userAttempts.get(t.id)?.passed);
  const closedTests = tests.filter(t => t.status === 'closed');
  const upcomingTests = tests.filter(t => t.status === 'upcoming');

  const TestCard = ({ test }: { test: Test }) => {
    const userAttempt = userAttempts.get(test.id);
    const isClosed = test.status === 'closed';
    const isUpcoming = test.status === 'upcoming';
    const isDisabled = isClosed || isUpcoming;

    return (
      <div
        className={`rounded-lg p-6 border transition-all ${
          isClosed
            ? 'bg-gray-50 border-gray-300 opacity-75'
            : isUpcoming
            ? 'bg-gray-50 border-gray-300 opacity-75'
            : test.type === 'final_exam'
            ? 'bg-blue-50 border-blue-400 shadow-sm'
            : 'bg-white border-gray-300 hover:border-blue-400'
        }`}
      >
        {/* Badge row: test type + status */}
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs font-bold px-3 py-1 rounded-full ${
              test.type === 'final_exam'
                ? 'bg-blue-600 text-white'
                : test.type === 'pre_assessment'
                ? 'bg-purple-100 text-purple-700'
                : 'bg-gray-100 text-gray-700'
            }`}>
              {test.type === 'final_exam' ? t('event.finalExam') :
               test.type === 'pre_assessment' ? t('event.preAssessment') :
               t('event.knowledgeCheck')}
            </span>

            {/* Status badge for closed/upcoming */}
            {isClosed && (
              <span className="text-xs font-medium px-2 py-1 rounded-full bg-gray-200 text-gray-600 flex items-center gap-1">
                <Lock className="h-3 w-3" />
                {t('event.test.closed')}
              </span>
            )}
            {isUpcoming && (
              <span className="text-xs font-medium px-2 py-1 rounded-full bg-yellow-200 text-yellow-700 flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {t('event.test.upcoming')}
              </span>
            )}
          </div>

        </div>

        <h3 className={`text-xl font-bold mb-2 ${isClosed ? 'text-gray-700' : ''}`}>{test.title}</h3>
        {test.description && (
          <p className={`mb-4 text-sm ${isClosed ? 'text-gray-500' : 'text-gray-600'}`}>{test.description}</p>
        )}

        {/* User's result for closed tests they've attempted */}
        {isClosed && userAttempt && (
          <div className={`mb-4 p-3 rounded-lg border ${
            userAttempt.passed
              ? 'bg-green-50 border-green-200'
              : 'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-center gap-2">
              {userAttempt.passed ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : (
                <XCircle className="h-5 w-5 text-red-600" />
              )}
              <span className={`font-semibold ${userAttempt.passed ? 'text-green-700' : 'text-red-700'}`}>
                {userAttempt.passed ? t('event.testHistory.passed') : t('event.testHistory.failed')}
              </span>
              <span className={`font-bold ${userAttempt.passed ? 'text-green-600' : 'text-red-600'}`}>
                {userAttempt.bestScore}%
              </span>
            </div>
          </div>
        )}

        {/* Test info - only show for non-closed or not-attempted closed tests */}
        {(!isClosed || !userAttempt) && (
          <div className={`grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm mb-4 ${isClosed ? 'text-gray-500' : 'text-gray-700'}`}>
            {test.resolved_from && (
              <div className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4 text-gray-500 flex-shrink-0" />
                <span>
                  <span className="font-semibold">{t('event.test.startDate')}:</span>{' '}
                  {(() => {
                    const d = new Date(test.resolved_from);
                    const isMidnight = d.getHours() === 0 && d.getMinutes() === 0;
                    return d.toLocaleDateString(language === 'uk' ? 'uk-UA' : 'en-GB', {
                      day: 'numeric', month: 'short', year: 'numeric',
                      ...(isMidnight ? {} : { hour: '2-digit', minute: '2-digit' }),
                    });
                  })()}
                </span>
              </div>
            )}
            {test.resolved_until && (
              <div className="flex items-center gap-1.5">
                <Calendar className="h-4 w-4 text-gray-500 flex-shrink-0" />
                <span>
                  <span className="font-semibold">{t('event.test.deadline')}:</span>{' '}
                  {(() => {
                    const d = new Date(test.resolved_until);
                    const isMidnight = d.getHours() === 0 && d.getMinutes() === 0;
                    return d.toLocaleDateString(language === 'uk' ? 'uk-UA' : 'en-GB', {
                      day: 'numeric', month: 'short', year: 'numeric',
                      ...(isMidnight ? {} : { hour: '2-digit', minute: '2-digit' }),
                    });
                  })()}
                </span>
              </div>
            )}
            {!!test.question_count && (
              <div className="flex items-center gap-1.5">
                <ListChecks className="h-4 w-4 text-gray-500 flex-shrink-0" />
                <span className="font-semibold">{test.question_count} {t('event.test.questionsCount').toLowerCase()}</span>
              </div>
            )}
            <div className="flex items-center gap-1.5">
              <Award className="h-4 w-4 text-gray-500 flex-shrink-0" />
              <span><span className="font-semibold">{t('courseDetails.passingScore')}:</span> {test.passing_score}%</span>
            </div>
            <div className="flex items-center gap-1.5">
              <RefreshCw className="h-4 w-4 text-gray-500 flex-shrink-0" />
              <span>
                {test.max_attempts ? (
                  <><span className="font-semibold">{t('event.test.attemptsRemaining')}:</span> {test.max_attempts - (userAttempt?.attemptCount ?? 0)} / {test.max_attempts}</>
                ) : (
                  t('event.test.attemptsUnlimited')
                )}
              </span>
            </div>
            {test.time_limit_minutes && (
              <div className="flex items-center gap-1.5">
                <Clock className="h-4 w-4 text-gray-500 flex-shrink-0" />
                <span className="font-semibold">{test.time_limit_minutes} {t('common.minutes')}</span>
              </div>
            )}
          </div>
        )}

        {/* Action button */}
        {!isDisabled ? (
          <Button
            onClick={() => startTest(test)}
            className="w-full"
          >
            {t('event.test.startTest')}
          </Button>
        ) : isClosed && !userAttempt ? (
          <div className="text-center text-sm text-gray-500 py-2">
            {t('event.test.missedTest')}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Open tests */}
      {openTests.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-2xl font-bold">{t('event.test.availableTestsTitle')}</h2>
          <div className="space-y-4">
            {openTests.map((test) => (
              <TestCard key={test.id} test={test} />
            ))}
          </div>
        </div>
      )}

      {/* Closed tests */}
      {closedTests.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-gray-700">{t('event.test.closedTestsTitle')}</h2>
          <div className="space-y-4">
            {closedTests.map((test) => (
              <TestCard key={test.id} test={test} />
            ))}
          </div>
        </div>
      )}

      {/* Upcoming tests */}
      {upcomingTests.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-yellow-700">{t('event.test.upcomingTestsTitle')}</h2>
          <div className="space-y-4">
            {upcomingTests.map((test) => (
              <TestCard key={test.id} test={test} />
            ))}
          </div>
        </div>
      )}

      {/* Test History Section */}
      <TestHistory eventId={eventId} enrollmentId={enrollmentId} onViewAttempt={viewAttemptResults} showDivider={openTests.length > 0 || closedTests.length > 0 || upcomingTests.length > 0} />

      {/* Test Taking Overlay */}
      {overlayTest && user && (
        <TestTakingOverlay
          test={overlayTest.test}
          contentTitle={contentTitle}
          userId={user.id}
          enrollmentId={enrollmentId}
          eventId={eventId}
          attemptCount={overlayTest.attemptCount}
          onClose={handleOverlayClose}
          onComplete={handleOverlayComplete}
        />
      )}
    </div>
  );
}
